import React from 'react';
import { 
  Zap, 
  Leaf, 
  TrendingDown, 
  Home, 
  Shield, 
  Sun,
  Battery,
  Car,
  Thermometer,
  Wind,
  CheckCircle2,
  ArrowRight
} from 'lucide-react';
import { Button } from './ui/button';

export const ElectrificationSection = ({ onCompareNow }) => {
  const benefits = [
    {
      icon: TrendingDown,
      title: 'Lower Energy Bills',
      description: 'Electric appliances are up to 3x more efficient than gas. Combined with solar, many households eliminate their energy bills entirely.',
      stat: 'Up to 70%',
      statLabel: 'energy savings'
    },
    {
      icon: Leaf,
      title: 'Reduce Carbon Footprint',
      description: 'Electrifying your home powered by renewable energy dramatically reduces your household emissions and environmental impact.',
      stat: '80%',
      statLabel: 'emission reduction'
    },
    {
      icon: Shield,
      title: 'Future-Proof Your Home',
      description: 'As gas prices rise and renewable energy costs fall, an electrified home protects you from future energy price volatility.',
      stat: '25+ years',
      statLabel: 'system lifespan'
    },
    {
      icon: Home,
      title: 'Increase Property Value',
      description: 'Energy-efficient, fully electrified homes command higher prices and sell faster in today\'s environmentally conscious market.',
      stat: '4-7%',
      statLabel: 'value increase'
    }
  ];

  const electrificationJourney = [
    {
      step: 1,
      icon: Sun,
      title: 'Solar Power',
      description: 'Start generating your own clean electricity',
      color: 'amber'
    },
    {
      step: 2,
      icon: Battery,
      title: 'Battery Storage',
      description: 'Store excess energy for night-time use',
      color: 'blue'
    },
    {
      step: 3,
      icon: Thermometer,
      title: 'Heat Pump Hot Water',
      description: 'Replace gas hot water with efficient electric',
      color: 'red'
    },
    {
      step: 4,
      icon: Wind,
      title: 'Electric Heating & Cooling',
      description: 'Efficient reverse-cycle air conditioning',
      color: 'cyan'
    },
    {
      step: 5,
      icon: Car,
      title: 'EV Charging',
      description: 'Power your car from your roof',
      color: 'green'
    }
  ];

  return (
    <section id="electrification" className="py-16 md:py-20 bg-gradient-to-b from-gray-900 via-gray-800 to-gray-900 text-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        
        {/* Header */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 bg-cyo-orange/20 px-4 py-2 rounded-full mb-4">
            <Zap className="w-4 h-4 text-cyo-orange" />
            <span className="text-sm font-semibold text-cyo-orange">Home Electrification</span>
          </div>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-4">
            The Future of Home Energy is{' '}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyo-orange to-yellow-400">
              Electric
            </span>
          </h2>
          <p className="text-lg text-gray-300 max-w-3xl mx-auto">
            Home electrification means replacing gas appliances with efficient electric alternatives, 
            powered by rooftop solar. It's the smartest way to slash your energy bills and reduce your 
            environmental impact.
          </p>
        </div>

        {/* Benefits Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
          {benefits.map((benefit, index) => {
            const Icon = benefit.icon;
            return (
              <div 
                key={index} 
                className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-6 hover:bg-white/10 transition-all duration-300"
              >
                <div className="w-12 h-12 bg-cyo-orange/20 rounded-xl flex items-center justify-center mb-4">
                  <Icon className="w-6 h-6 text-cyo-orange" />
                </div>
                <h3 className="text-xl font-semibold text-white mb-2">{benefit.title}</h3>
                <p className="text-gray-400 text-sm mb-4">{benefit.description}</p>
                <div className="pt-4 border-t border-white/10">
                  <span className="text-2xl font-bold text-cyo-orange">{benefit.stat}</span>
                  <span className="text-sm text-gray-400 ml-2">{benefit.statLabel}</span>
                </div>
              </div>
            );
          })}
        </div>

        {/* The Electrification Journey */}
        <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-3xl p-8 md:p-12">
          <h3 className="text-2xl md:text-3xl font-bold text-center mb-8">
            Your Electrification Journey
          </h3>
          <p className="text-center text-gray-300 mb-10 max-w-2xl mx-auto">
            Every home is different, but here's a typical pathway to a fully electrified, 
            energy-independent home. Start anywhere that makes sense for you.
          </p>

          {/* Journey Steps */}
          <div className="flex flex-wrap justify-center gap-4 mb-10">
            {electrificationJourney.map((item, index) => {
              const Icon = item.icon;
              const colorClasses = {
                amber: 'bg-amber-500/20 text-amber-400 border-amber-500/30',
                blue: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
                red: 'bg-red-500/20 text-red-400 border-red-500/30',
                cyan: 'bg-cyan-500/20 text-cyan-400 border-cyan-500/30',
                green: 'bg-green-500/20 text-green-400 border-green-500/30'
              };
              
              return (
                <div key={index} className="flex items-center">
                  <div className={`relative flex flex-col items-center p-4 rounded-2xl border ${colorClasses[item.color]} min-w-[140px]`}>
                    <div className="absolute -top-3 -left-3 w-7 h-7 bg-cyo-orange rounded-full flex items-center justify-center text-white text-sm font-bold">
                      {item.step}
                    </div>
                    <Icon className="w-8 h-8 mb-2" />
                    <span className="font-semibold text-white text-sm text-center">{item.title}</span>
                    <span className="text-xs text-gray-400 text-center mt-1">{item.description}</span>
                  </div>
                  {index < electrificationJourney.length - 1 && (
                    <ArrowRight className="w-5 h-5 text-gray-600 mx-2 hidden md:block" />
                  )}
                </div>
              );
            })}
          </div>

          {/* Key Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            <div className="text-center p-4">
              <div className="text-3xl md:text-4xl font-bold text-cyo-orange mb-1">$2,000+</div>
              <div className="text-sm text-gray-400">Average annual savings</div>
            </div>
            <div className="text-center p-4">
              <div className="text-3xl md:text-4xl font-bold text-green-400 mb-1">5-7 yrs</div>
              <div className="text-sm text-gray-400">Typical payback period</div>
            </div>
            <div className="text-center p-4">
              <div className="text-3xl md:text-4xl font-bold text-blue-400 mb-1">90%</div>
              <div className="text-sm text-gray-400">Self-sufficiency possible</div>
            </div>
            <div className="text-center p-4">
              <div className="text-3xl md:text-4xl font-bold text-amber-400 mb-1">$0</div>
              <div className="text-sm text-gray-400">Gas bills after electrification</div>
            </div>
          </div>

          {/* CTA */}
          <div className="text-center">
            <Button
              onClick={onCompareNow}
              className="bg-cyo-orange hover:bg-cyo-orange-dark text-white font-bold px-8 py-6 text-lg rounded-xl shadow-lg shadow-orange-500/30 hover:shadow-xl transition-all duration-300"
            >
              Start Your Electrification Journey
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </div>
        </div>

        {/* Why Electrify Now */}
        <div className="mt-16 grid md:grid-cols-2 gap-8 items-center">
          <div>
            <h3 className="text-2xl md:text-3xl font-bold mb-6">
              Why Electrify Your Home <span className="text-cyo-orange">Now?</span>
            </h3>
            <div className="space-y-4">
              <div className="flex items-start gap-3">
                <CheckCircle2 className="w-6 h-6 text-green-400 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-semibold text-white">Government Rebates Available</p>
                  <p className="text-gray-400 text-sm">Take advantage of current federal and state incentives before they change</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <CheckCircle2 className="w-6 h-6 text-green-400 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-semibold text-white">Gas Prices Rising</p>
                  <p className="text-gray-400 text-sm">Natural gas costs have increased significantly and are projected to continue rising</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <CheckCircle2 className="w-6 h-6 text-green-400 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-semibold text-white">Technology is Mature</p>
                  <p className="text-gray-400 text-sm">Heat pumps, batteries, and EVs are now reliable, efficient, and affordable</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <CheckCircle2 className="w-6 h-6 text-green-400 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-semibold text-white">Grid Independence</p>
                  <p className="text-gray-400 text-sm">Protect yourself from blackouts and energy price spikes with battery backup</p>
                </div>
              </div>
            </div>
          </div>
          
          <div className="bg-gradient-to-br from-cyo-orange/20 to-amber-500/10 rounded-3xl p-8 border border-orange-500/20">
            <h4 className="text-xl font-bold text-white mb-4">Did You Know?</h4>
            <p className="text-gray-300 mb-4">
              A typical Australian household can save <span className="text-cyo-orange font-bold">$2,000 to $4,000 per year</span> by 
              fully electrifying with solar. Over 25 years, that's up to <span className="text-green-400 font-bold">$100,000 in savings</span>.
            </p>
            <p className="text-gray-300 mb-4">
              Plus, you'll be producing clean energy, reducing your carbon footprint by the equivalent of 
              <span className="text-green-400 font-bold"> planting 150+ trees</span> every year.
            </p>
            <div className="flex items-center gap-2 text-sm text-gray-400">
              <Leaf className="w-4 h-4 text-green-400" />
              <span>Good for your wallet. Great for the planet.</span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
